import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { PageLoader } from '../Common/page-loader';
import { navigation, NavigationItem } from './sidebar';
import { Copilot } from '@/features/copilot';
import { useSitesWithLatestData } from '@/api/hooks/useSites';
import { format } from 'date-fns';
import { Sun } from 'lucide-react';

interface PageWrapperProps {
  isAuthenticated?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  sidebar?: React.ReactNode;
}

export function PageWrapper({
  isAuthenticated = true,
  isLoading = false,
  error = null,
  sidebar,
}: PageWrapperProps) {
  const location = useLocation();

  // Fetch sites data with latest_data for header display
  const { data: sitesData } = useSitesWithLatestData();
  const currentSite = sitesData?.data?.[0];

  // State for current time
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get the current page title based on the route
  const getCurrentPageTitle = () => {
    const currentPath = location.pathname;

    // Special case for nested routes
    if (currentPath.startsWith('/autopilot/automation/')) {
      return 'Automation Details';
    }
    if (currentPath.startsWith('/autopilot/create')) {
      return 'Create Automation';
    }

    // Find matching navigation item
    const currentNavItem = navigation.find((item: NavigationItem) =>
      currentPath === item.href || currentPath.startsWith(`${item.href}/`)
    );

    return currentNavItem?.name || 'Alto CERO';
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-bg">
        <div className="text-center p-8 bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/80 shadow-sm">
          <h1 className="text-2xl font-semibold text-dark-blue mb-2">
            {error.name}
          </h1>
          <p className="text-dark-grey mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gradient-to-r from-primary to-blue-400 text-white rounded-lg hover:shadow-md transition-all duration-200"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="h-screen bg-bg flex overflow-hidden">
      {/* Sidebar - now fixed position */}
      <aside className="fixed inset-y-0 left-0 z-20">
        {sidebar}
      </aside>

      {/* Copilot - Now integrated in the header */}
      {location.pathname !== '/copilot' && (
        <Copilot theme="light" />
      )}

      {/* Main Content */}
      <main className={cn(
        "flex-1 flex flex-col h-full",
        sidebar && "ml-[3.75rem]" // Adjust to match sidebar width
      )}>
        {/* Header */}
        <header className={cn(
          "bg-white border-b border-gray-200 z-10 flex-shrink-0",
          location.pathname === '/copilot' && "z-20" // Higher z-index for Copilot page
        )}>
          <div className="pl-4 pr-2 h-12 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-primary to-green">
                {getCurrentPageTitle()}
              </h1>
              <div className="h-6 w-px bg-gray-200/80" />
              
            </div>

            <div className="flex items-center gap-6">
              {/* Modern Minimal Info Display */}
              {currentSite && (
                <div className="flex items-center gap-6">
                  {/* Site Name - Subtle */}
                  <div className="paragraph-12 text-dark-grey">
                    {currentSite.name}
                  </div>
                  
                  {/* Temperature Badge */}
                  {currentSite.latest_data?.outdoor_temperature?.value != null && (
                    <div className="flex items-center gap-1.5 bg-light-grey/50 px-3 py-1 rounded-full">
                      <Sun className="w-3.5 h-3.5 text-orange" />
                      <span className="paragraph-12 font-medium text-black">
                        {currentSite.latest_data.outdoor_temperature.value.toFixed(1)}°
                      </span>
                    </div>
                  )}
                  
                  {/* DateTime - Compact */}
                  <div className="flex items-center gap-2">
                    <span className="paragraph-12 font-medium text-black tabular-nums">
                      {format(currentTime, 'h:mm a')}
                    </span>
                    <span className="paragraph-11 text-dark-grey">
                      {format(currentTime, 'EEE d MMM')} · ICT
                    </span>
                  </div>
                </div>
              )}
              
              {/* Copilot Button */}
              {location.pathname !== '/copilot' && (
                <div id="copilot-button-container" className="relative h-10 w-10 ml-auto">
                  {/* The CopilotButton component will render here */}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 relative min-h-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}